import CopyButton from "@components/buttons/CopyButton";
import { PrimeTradeData } from "@services/models/Prime/GetPrimeTradeListModel";
import React from "react";
import ConstantFunctions from "src/utils/constants_functions";
import { formatAdaptiveDuration } from "../utils/formatAdaptiveDuration";
import { ActiveDurationPill, ActiveDurationTone } from "./PrimeOrderPills";

const constantFunctions = new ConstantFunctions();

const EVENT_IDS = {
  RECEIVED: "1",
  ACCEPTED: "2",
  CONFIRMED: "3",
  COMPLETED: "4",
  COMPLETED_ALT: "6",
  DISPUTED: "5",
  EXPIRED: "9",
};

export const DateCellRenderer = (params: any) => {
  return (
    <span className="text-[10px] text-text-base font-medium text-gray-700">
      {constantFunctions.dateFormatInDDMMYY(params.value)}
    </span>
  );
};

export const TradeIdCellRenderer = (params: any) => {
  const data = params.data as PrimeTradeData;
  return (
    <div className="flex items-center justify-start h-full">
      <div className="flex items-center justify-start">
        <span
          className={`text-[10px] font-medium px-[6px] h-[24px] rounded flex items-center mr-1 ${
            data.tradeType === "B" ? "bg-bg-status-green" : "bg-bg-status-red"
          } text-text-base`}
        >
          {data.tradeType}
        </span>
        <span className="mr-1 text-[10px] text-text-base font-medium text-gray-700">
          {data.tradeId}
        </span>
      </div>
      <CopyButton
        copyText={data.tradeId.toString()}
        copyMessage="Trade ID copied!"
        copyIconSize={10}
        buttonSize="14px"
        borderRadius="2px"
      />
    </div>
  );
};

export const MerchantOrderIdCellRenderer = (params: any) => {
  const data = params.data as PrimeTradeData;
  return (
    <div className="flex flex-row items-center gap-1">
      <span className="text-[10px] text-text-base font-medium">
        {data.merchantOrderId || "- -"}
      </span>
      {data.merchantOrderId && (
        <CopyButton
          copyText={data.merchantOrderId}
          copyMessage="Merchant Order ID copied!"
          copyIconSize={10}
          buttonSize="14px"
          borderRadius="2px"
        />
      )}
    </div>
  );
};

export const AmountCellRenderer = (params: any) => {
  const data = params.data as PrimeTradeData;
  return (
    <div className="flex items-center text-[10px] text-text-base font-medium text-gray-700">
      <div className="flex items-center">
        <img
          src={data.cryptoCurrencyImage}
          alt=""
          className="h-[12px] w-[12px] mr-1"
        />
        <span>{data.cryptoAmount?.toFixed(2) ?? "--"}</span>
      </div>
      <span className="mx-1">|</span>
      <div className="flex items-center">
        <img
          src={data.fiatCurrencyImage}
          alt=""
          className="h-[12px] w-[12px] mr-1"
        />
        <span>{data.fiatAmount?.toFixed(2)}</span>
      </div>
    </div>
  );
};

const getEventTimeDiffMs = (data: PrimeTradeData, eventId: string): number => {
  if (!data.eventDetails || data.eventDetails.length === 0) {
    return 0;
  }

  const sortedEvents = [...data.eventDetails].sort((a, b) => {
    return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
  });

  const eventIndex = sortedEvents.findIndex((e) => e.eventId === eventId);
  if (eventIndex === -1) {
    return 0;
  }

  const currentEventTime = new Date(
    sortedEvents[eventIndex].timestamp,
  ).getTime();
  const prevEventTime =
    eventIndex === 0
      ? new Date(data.createdAt).getTime()
      : new Date(sortedEvents[eventIndex - 1].timestamp).getTime();

  return currentEventTime - prevEventTime;
};

const getEventStatus = (
  data: PrimeTradeData,
  eventId: string,
): React.ReactNode => {
  if (eventId === EVENT_IDS.RECEIVED || eventId === EVENT_IDS.EXPIRED) {
    const hasEvent = data.eventDetails?.some((e) => e.eventId === eventId);
    if (!hasEvent) return null;

    let color = "bg-bg-button-secondary";
    if (eventId === EVENT_IDS.EXPIRED) {
      color = "bg-bg-header";
    }

    return (
      <div
        className={`rounded-[6px] w-[25px] h-[18px] p-[4px] text-[10px] text-text-base flex items-center justify-center ${color}`}
      ></div>
    );
  }

  const eventIndex = data.eventDetails?.findIndex((e) => e.eventId === eventId);
  if (eventIndex === -1 || eventIndex === undefined) return null;

  const { display, tooltip } = formatAdaptiveDuration(
    getEventTimeDiffMs(data, eventId),
  );

  let color = "bg-bg-button-secondary";
  if (eventId === EVENT_IDS.COMPLETED || eventId === EVENT_IDS.COMPLETED_ALT) {
    color = "bg-bg-helper-green-dark";
  } else if (eventId === EVENT_IDS.DISPUTED) {
    color = "bg-[#FFF0E4]";
  }

  return (
    <div
      title={tooltip}
      className={`rounded-[6px] text-center px-[4px] py-[4px] text-[10px] h-[24px] min-w-[48px] text-text-base flex items-center justify-center whitespace-nowrap ${color}`}
    >
      {display}
    </div>
  );
};

export const ReceivedCellRenderer = (params: any) => {
  return getEventStatus(params.data, EVENT_IDS.RECEIVED);
};

export const AcceptedCellRenderer = (params: any) => {
  return getEventStatus(params.data, EVENT_IDS.ACCEPTED);
};

export const ConfirmedCellRenderer = (params: any) => {
  return getEventStatus(params.data, EVENT_IDS.CONFIRMED);
};

export const CompletedCellRenderer = (params: any) => {
  return (
    getEventStatus(params.data, EVENT_IDS.COMPLETED) ||
    getEventStatus(params.data, EVENT_IDS.COMPLETED_ALT)
  );
};

export const DisputedCellRenderer = (params: any) => {
  return getEventStatus(params.data, EVENT_IDS.DISPUTED);
};

export const ExpiredCellRenderer = (params: any) => {
  return getEventStatus(params.data, EVENT_IDS.EXPIRED);
};

export const ActiveCellRenderer = (params: any) => {
  const data = params.data as PrimeTradeData;

  const durationFromMs = (diffMs: number, tone: ActiveDurationTone) => (
    <ActiveDurationPill diffMs={diffMs} tone={tone} />
  );

  const sortedEventDetailsFromApi = [...(data.eventDetails || [])].sort(
    (a, b) => {
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    },
  );

  if (data.eventDetails && data.eventDetails.length > 0) {
    const latestEvent = sortedEventDetailsFromApi[0];
    if (
      latestEvent.eventId === "9" ||
      latestEvent.eventId === "6" ||
      latestEvent.eventId === "4"
    ) {
      return durationFromMs(
        new Date(latestEvent.timestamp).getTime() -
          new Date(data.createdAt).getTime(),
        latestEvent.eventId === "9" ? "failed" : "completed",
      );
    }
  }

  return durationFromMs(
    Date.now() - new Date(data.createdAt).getTime(),
    "active",
  );
};
